import './styles.scss';

import { ChangeEvent, FocusEvent, ForwardedRef, MouseEvent, ReactElement, forwardRef, useImperativeHandle, useRef } from 'react';
import { cleanDateRegExp, formatDate, isValidTypedDate, toDateDisplay } from '@/common/DateInput/utils';

import { DATE_FORMAT } from '@/constants';
import DateInputPopover from '@/common/DateInput/DateInputPopover';
import { DateInputTypes } from '@/common/DateInput/types';
import PopoverInput from '@/common/PopoverInput';
import { getClasses } from '@/utils';

const DateInput = forwardRef<DateInputTypes.RefType, DateInputTypes.Props>(
  ({ format = DATE_FORMAT, value, ...props }: DateInputTypes.Props, ref: ForwardedRef<DateInputTypes.RefType>): ReactElement => {
    // Input Ref/State
    const inputRef = useRef<DateInputTypes.RefType>(null);

    useImperativeHandle(ref, () => {
      const input = inputRef.current;
      return input
        ? {
            ...input,
            focus: (): void => {
              input.focus();
            },
            blur: (): void => {
              input.blur();
            },
            show: (): void => {
              input.focus();
            },
            hide: (): void => {
              input.blur();
            },
            target: input.target,
          }
        : ({} as any);
    });

    // Handle Change
    const onChange = (
      newValue: string,
      focus: boolean = false,
      event?: ChangeEvent<HTMLInputElement> | FocusEvent<HTMLInputElement> | MouseEvent<HTMLInputElement>
    ): string => {
      props.onChange?.(newValue, (event || { target: inputRef.current }) as ChangeEvent<HTMLInputElement>);
      if (focus) inputRef.current?.focus();
      return newValue;
    };

    return (
      <>
        <PopoverInput
          placeholder={format}
          onFormat={(raw: string): string => (isValidTypedDate(raw, format) ? formatDate(format)(raw) : '')}
          {...props}
          type="format"
          // While typing show the raw digits/separators
          value={toDateDisplay(format)(value)}
          className={getClasses('DateInput', 'Common', props.className)}
          onChange={(newValue: string, event: ChangeEvent<HTMLInputElement>): void => {
            newValue = newValue.replace(cleanDateRegExp, '');
            event.target.value = onChange(newValue, false, event);
          }}
          onBlur={(newValue: string, event: FocusEvent<HTMLInputElement>): void => {
            // avoid dirtying an untouched field
            if (newValue !== toDateDisplay(format)(value)) onChange(newValue, false, event);
            props.onBlur?.(newValue, event);
          }}
          ref={inputRef}
        >
          <DateInputPopover value={value} format={format} target={inputRef} onChange={onChange} />
        </PopoverInput>
      </>
    );
  }
);
DateInput.displayName = 'DateInput';

export default DateInput;
